import { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Loader2, Receipt } from "lucide-react";

const Summary = ({ title, data, testid }) => (
  <div className="rounded border p-2 text-[11px]" data-testid={testid}>
    <div className="font-bold mb-1">{title}</div>
    {["vendas", "entram_no_caixa", "nao_entram_no_caixa", "contrato_visivel", "validade_ok", "indicador_ativo", "sem_problemas"].map((k) => (
      <div key={k}>{k}: <b>{data?.[k] ?? 0}</b></div>
    ))}
    <div className="font-bold mt-1">Problemas encontrados</div>
    {Object.entries(data?.problemas || {}).map(([k, v]) => <div key={k} className="break-words">{k}: <b>{v}</b></div>)}
    {!Object.keys(data?.problemas || {}).length && <div className="text-slate-500">nenhum</div>}
  </div>
);

const Row = ({ r }) => (
  <div className="border-t pt-1" data-testid="sales-audit-row">
    <b>{r.dia_brasilia}</b> · {r.aluno} · {r.plano} · R$ {r.valor.toFixed(2)} · {r.forma} · origem {r.origem} · vendedor {r.vendedor || "—"} · caixa: <b>{r.entra_no_caixa ? "sim" : "não"}</b> · contrato: <b>{r.contrato_visivel ? "sim" : "não"}</b> · indicador: <b>{r.indicador}</b>
    <ul className="list-disc pl-4 text-rose-700">{r.problemas.map((p, i) => <li key={i}>{p}</li>)}</ul>
  </div>
);

export const NextFitSalesAudit = ({ unitId }) => {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/integrations/nextfit/units/${unitId}/sales-audit`); setR(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha na auditoria"); }
    finally { setBusy(false); }
  };
  const download = async () => {
    try {
      const { data } = await api.get(`/integrations/nextfit/finance-audit/csv/${r.csv}`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(data); a.download = r.csv; a.click();
    } catch { toast.error("CSV indisponível — rode a auditoria novamente."); }
  };
  return (
    <Card className="p-5" data-testid="sales-audit-card">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2"><Receipt className="w-5 h-5 text-slate-700"/><h3 className="font-heading text-lg font-black text-slate-900">Auditoria de vendas pós-migração (somente leitura)</h3></div>
        <div className="flex gap-2">
          <Button onClick={run} disabled={busy} variant="outline" data-testid="sales-audit-btn">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Receipt className="w-4 h-4 mr-1"/>}Auditar vendas</Button>
          {r?.csv && <Button onClick={download} variant="outline" data-testid="sales-audit-csv-btn"><Download className="w-4 h-4 mr-1"/>CSV ({r.linhas_csv})</Button>}
        </div>
      </div>
      <p className="text-xs text-slate-500">Lista as vendas feitas depois da migração para alunos com histórico Next Fit e confere caixa do dia, unidade, contrato, validade e indicador Ativo/Inativo. Nada é gravado.</p>
      {r && (
        <div className="mt-3 space-y-3" data-testid="sales-audit-report">
          <div className="text-xs font-bold">Nada foi gravado · migração em {r.migracao_em?.slice(0, 16) || "—"} · {r.alunos_migrados} alunos migrados · {r.vendas_analisadas} vendas analisadas · {r.generated_at?.slice(0, 16)}</div>
          {r.observacao && <div className="text-xs text-slate-600">{r.observacao}</div>}
          {r.migrados && (
            <div className="grid md:grid-cols-2 gap-3">
              <Summary title="Alunos migrados (Next Fit)" data={r.migrados} testid="sales-audit-migrated"/>
              <Summary title="Comparação: alunos nativos (últimas 200 vendas)" data={r.nativos_comparacao} testid="sales-audit-natives"/>
            </div>
          )}
          {!!r.exemplos?.length && (
            <div className="rounded border p-2 text-[11px] max-h-96 overflow-auto" data-testid="sales-audit-examples">
              <div className="font-bold">Vendas com inconsistência (até 50; lista completa no CSV)</div>
              {r.exemplos.map((x) => <Row key={x.session_id} r={x}/>)}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
